import React, { useState, useRef, useEffect, useMemo, useId } from "react";

function renderMarkdown(text) {
  if (!text) return "";

  // Escape HTML
  let html = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

  // Bold: **text** or __text__
  html = html.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  html = html.replace(/__(.+?)__/g, "<strong>$1</strong>");

  // Italic: *text* or _text_ (but not inside bold)
  html = html.replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, "<em>$1</em>");

  // Inline code: `text`
  html = html.replace(/`(.+?)`/g, '<code class="bg-[#F6F4EE] text-[#111111] px-1.5 py-0.5 rounded text-xs font-mono">$1</code>');

  // Links: [label](url)
  html = html.replace(
    /\[([^\]]+)\]\(([^)\s]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer" class="font-medium text-[#111111] underline underline-offset-2 hover:text-[#3A3A36]">$1</a>'
  );

  // Split into lines for block-level processing
  const lines = html.split("\n");
  const result = [];
  let inList = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const listMatch = line.match(/^(\s*)[-*]\s+(.*)/);

    if (listMatch) {
      if (!inList) {
        result.push('<ul class="list-disc list-inside space-y-1 my-2">');
        inList = true;
      }
      result.push(`<li>${listMatch[2]}</li>`);
    } else {
      if (inList) {
        result.push("</ul>");
        inList = false;
      }
      // Headings
      if (line.match(/^### /)) {
        result.push(`<h3 class="font-semibold text-base mt-3 mb-1">${line.replace(/^### /, "")}</h3>`);
      } else if (line.match(/^## /)) {
        result.push(`<h2 class="font-semibold text-base mt-3 mb-1">${line.replace(/^## /, "")}</h2>`);
      } else if (line.trim() === "") {
        result.push('<div class="h-2"></div>');
      } else {
        result.push(`<p>${line}</p>`);
      }
    }
  }

  if (inList) {
    result.push("</ul>");
  }

  return result.join("");
}

function MarkdownContent({ content }) {
  const html = useMemo(() => renderMarkdown(content), [content]);
  return (
    <div
      className="text-sm leading-relaxed [&_strong]:font-semibold [&_ul]:ml-0 [&_li]:text-sm [&_p]:mb-0.5 [&_h2]:text-sm [&_h3]:text-sm"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

function isTransferApprovalPrompt(message) {
  return message.role === "assistant" && /do you approve this transfer\?/i.test(message.content || "");
}

// Parses a `:::receipt` marker block (title + download URL) emitted by the server
// for confirmation receipts, so it can be rendered as a download callout card.
function parseReceipt(content) {
  if (!content) return null;
  const match = content.match(/^:::receipt\s*\n([\s\S]*?)\n:::\s*$/);
  if (!match) return null;

  const lines = match[1].split("\n").map((line) => line.trim()).filter(Boolean);
  const url = lines.find((line) => /^https?:\/\//i.test(line));
  if (!url) return null;
  const title = lines.find((line) => !/^https?:\/\//i.test(line)) || "Confirmation receipt";
  return { title, url };
}

function ReceiptCallout({ title, url }) {
  return (
    <a
      href={url}
      className="group flex items-center gap-3 rounded-xl border border-[#E4E0D6] bg-[#FAF7EE] px-4 py-3 no-underline transition-colors duration-150 hover:border-[#111111] hover:bg-white"
    >
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white text-[#B3261E] shadow-sm">
        <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
        </svg>
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold text-[#111111]">{title}</span>
        <span className="block text-xs text-[#9A9890]">PDF &middot; Click to download</span>
      </span>
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#111111] text-white transition-transform duration-150 group-hover:translate-y-0.5">
        <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.8" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-6L12 15m0 0 4.5-4.5M12 15V3" />
        </svg>
      </span>
    </a>
  );
}

function AssistantAvatar() {
  return (
    <div className="shrink-0 mr-3">
      <div className="h-8 w-8 rounded-full bg-[#F6F4EE] flex items-center justify-center">
        <svg
          className="h-4 w-4 text-[#111111]"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth="1.5"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 0 0-2.455 2.456Z"
          />
        </svg>
      </div>
    </div>
  );
}

function MessageBubble({ message, showApprovalActions, onQuickReply, disabled }) {
  const isUser = message.role === "user";
  const receipt = !isUser ? parseReceipt(message.content) : null;

  if (receipt) {
    return (
      <div className="flex justify-start mb-4">
        <AssistantAvatar />
        <div className="max-w-[75%] w-full">
          <ReceiptCallout title={receipt.title} url={receipt.url} />
        </div>
      </div>
    );
  }

  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"} mb-4`}>
      {!isUser && <AssistantAvatar />}
      <div
        className={`max-w-[75%] rounded-2xl px-4 py-3 ${
          isUser
            ? "bg-[#111111] text-white"
            : "bg-white border border-[#E4E0D6] text-[#3A3A36]"
        }`}
      >
        {isUser ? (
          <div className="text-sm whitespace-pre-wrap leading-relaxed">
            {message.content}
          </div>
        ) : (
          <>
            <MarkdownContent content={message.content} />
            {showApprovalActions && (
              <div className="mt-3 flex flex-wrap gap-2 border-t border-[#E4E0D6] pt-3">
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => onQuickReply("Yes")}
                  className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  aria-label="Approve transfer"
                >
                  <svg
                    className="h-4 w-4"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.8"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                  </svg>
                  Yes
                </button>
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => onQuickReply("No")}
                  className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-[#E4E0D6] bg-white px-3 py-2 text-sm font-medium text-[#6B6B66] transition-colors duration-150 hover:bg-[#F6F4EE] hover:text-[#111111] focus:outline-none focus:ring-2 focus:ring-[#111111] focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  aria-label="Cancel transfer"
                >
                  <svg
                    className="h-4 w-4"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.8"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                  </svg>
                  No
                </button>
              </div>
            )}
          </>
        )}
      </div>
      {isUser && (
        <div className="flex-shrink-0 ml-3">
          <div className="h-8 w-8 rounded-full bg-[#EDEAE0] flex items-center justify-center">
            <span className="text-xs font-medium text-[#6B6B66]">U</span>
          </div>
        </div>
      )}
    </div>
  );
}

const ALL_ENVIRONMENTS = [
  { value: "sandbox", label: "Sandbox", devOnly: true },
  { value: "nz", label: "NZ" },
  { value: "au", label: "AU" },
];

function airwallexEnvironmentLabel(environment) {
  switch (environment) {
    case "sandbox":
      return "Airwallex Sandbox";
    case "nz":
      return "Airwallex NZ";
    case "au":
      return "Airwallex AU";
    default:
      return "Airwallex";
  }
}

function duplicateReferenceText(warning) {
  if (!warning) return "";

  const verb = warning.submitted ? "was already submitted" : "already exists";
  let text = `Reference "${warning.reference}" ${verb} on transfer #${warning.transfer_id} (${warning.status})`;
  if (warning.transfer_amount) {
    text += ` for ${warning.transfer_amount} ${warning.transfer_currency || ""}`.trimEnd();
  }
  if (warning.count > 1) {
    text += ` and ${warning.count - 1} other transfer(s)`;
  }
  return `${text}. Check before continuing so you do not pay the same invoice twice.`;
}

const ACTIVE_TRANSFER_STATUSES = ["draft", "extracting", "pending_confirmation"];
const BUSY_UPLOAD_STATUSES = ["creating", "uploading", "extracting"];

function upsertTransfer(transfers, transfer) {
  if (!transfer) return transfers;
  const exists = transfers.some((item) => item.id === transfer.id);
  if (exists) {
    return transfers.map((item) => (item.id === transfer.id ? transfer : item));
  }
  return [transfer, ...transfers];
}

function shouldShowTransferDraft(transfer, uploadBusy) {
  if (!transfer) return false;
  if (uploadBusy || transfer.status === "extracting") return false;
  if (transfer.source !== "invoice_pdf") return true;

  return transfer.status === "pending_confirmation" || (transfer.attachments || []).length > 0;
}

async function postJson(url, body, csrfToken) {
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken,
    },
    body: JSON.stringify(body || {}),
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || "Request failed");
  }
  return data;
}

function uploadPdfToCloudinary(file, cloudinary, onProgress) {
  return new Promise((resolve, reject) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", cloudinary.upload_preset);
    formData.append("folder", cloudinary.folder);

    const xhr = new XMLHttpRequest();
    xhr.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    });
    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText));
        } catch {
          reject(new Error("Cloudinary returned an invalid response"));
        }
      } else {
        reject(new Error(`Cloudinary upload failed (${xhr.status})`));
      }
    });
    xhr.addEventListener("error", () => reject(new Error("Cloudinary upload failed")));
    xhr.open("POST", `https://api.cloudinary.com/v1_1/${cloudinary.cloud_name}/auto/upload`);
    xhr.send(formData);
  });
}

function EnvironmentSelector({ chatId, environment, onChange, disabled, isDev }) {
  const environments = isDev
    ? ALL_ENVIRONMENTS
    : ALL_ENVIRONMENTS.filter((env) => !env.devOnly);

  return (
    <div className="flex items-center space-x-1 bg-[#F6F4EE] rounded-lg p-0.5">
      {environments.map((env) => (
        <button
          key={env.value}
          type="button"
          disabled={disabled}
          onClick={() => onChange(env.value)}
          className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all duration-150 ${
            environment === env.value
              ? "bg-white text-[#111111] shadow-sm"
              : "text-[#6B6B66] hover:text-[#111111]"
          } ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
        >
          {env.label}
        </button>
      ))}
    </div>
  );
}

function LoadingIndicator() {
  return (
    <div className="flex justify-start mb-4">
      <div className="flex-shrink-0 mr-3">
        <div className="h-8 w-8 rounded-full bg-[#F6F4EE] flex items-center justify-center">
          <svg
            className="h-4 w-4 text-[#111111]"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 0 0-2.455 2.456Z"
            />
          </svg>
        </div>
      </div>
      <div className="bg-white border border-[#E4E0D6] rounded-2xl px-4 py-3">
        <div className="flex items-center space-x-1.5">
          <div className="h-2 w-2 bg-[#9A9890] rounded-full animate-bounce" style={{ animationDelay: "0ms" }}></div>
          <div className="h-2 w-2 bg-[#9A9890] rounded-full animate-bounce" style={{ animationDelay: "150ms" }}></div>
          <div className="h-2 w-2 bg-[#9A9890] rounded-full animate-bounce" style={{ animationDelay: "300ms" }}></div>
        </div>
      </div>
    </div>
  );
}

function formatCurrency(amount, currency) {
  try {
    return new Intl.NumberFormat("en-NZ", { style: "currency", currency }).format(amount);
  } catch {
    return `${currency} ${Number(amount).toFixed(2)}`;
  }
}

function formatDate(dateStr) {
  const date = new Date(dateStr + "T00:00:00");
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffMs = date - today;
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

  const formatted = date.toLocaleDateString("en-NZ", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });

  if (diffDays === 0) return `Today`;
  if (diffDays === 1) return `Tomorrow`;
  if (diffDays <= 7) return `${formatted} (${diffDays}d)`;
  return formatted;
}

function ScheduledTransfers({ chatId, environment }) {
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`/airwallex_chats/${chatId}/scheduled_transfers`);
        const data = await res.json();
        if (!cancelled) {
          if (res.ok) {
            setTransfers(data.transfers || []);
          } else {
            setError(data.error || "Failed to load");
          }
        }
      } catch (err) {
        if (!cancelled) setError("Failed to load scheduled transfers");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [chatId, environment]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <div className="flex items-center space-x-2 text-[#9A9890]">
          <svg className="h-5 w-5 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <span className="text-sm">Loading transfers...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center px-6">
        <div className="p-3 bg-red-50 rounded-xl mb-3">
          <svg className="h-6 w-6 text-red-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
          </svg>
        </div>
        <p className="text-sm text-red-600">{error}</p>
      </div>
    );
  }

  if (transfers.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center px-6">
        <div className="p-4 bg-[#F6F4EE] rounded-2xl mb-4">
          <svg className="h-8 w-8 text-[#9A9890]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
          </svg>
        </div>
        <h3 className="text-base font-semibold text-[#111111] mb-1">No Scheduled Transfers</h3>
        <p className="text-sm text-[#6B6B66] max-w-sm">
          There are no upcoming transfers scheduled. Ask me to create one.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center h-full px-6 py-4">
      <div className="w-full max-w-lg">
        <div className="flex items-center space-x-2 mb-4">
          <svg className="h-5 w-5 text-[#111111]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
          </svg>
          <h3 className="text-sm font-semibold text-[#111111]">Scheduled Transfers</h3>
          <span className="text-xs text-[#6B6B66] bg-[#F6F4EE] px-2 py-0.5 rounded-full">{transfers.length}</span>
        </div>
        <div className="space-y-2">
          {transfers.map((t) => (
            <div key={t.id} className="bg-white border border-[#E4E0D6] rounded-xl px-4 py-3 flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-[#111111] truncate">{t.recipient}</p>
                <p className="text-xs text-[#9A9890] truncate">{t.reference || "—"}</p>
              </div>
              <div className="text-right ml-4 flex-shrink-0">
                <p className="text-sm font-semibold text-[#111111]">{formatCurrency(t.amount, t.currency)}</p>
                <p className="text-xs text-[#6B6B66] font-medium">{formatDate(t.transfer_date)}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SpinnerIcon({ className = "h-4 w-4" }) {
  return (
    <svg className={`${className} animate-spin`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-20" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-80" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}

function InvoiceUploadPanel({ uploadState, onFile, disabled }) {
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef(null);

  const chooseFile = () => {
    if (!disabled) inputRef.current?.click();
  };

  const handleFiles = (files) => {
    const file = files?.[0];
    if (file) onFile(file);
  };

  const statusText = {
    creating: "Preparing draft",
    uploading: `Uploading ${uploadState.progress || 0}%`,
    extracting: "Reading invoice",
    done: "Invoice loaded",
  }[uploadState.status];
  const busy = BUSY_UPLOAD_STATUSES.includes(uploadState.status);
  const progress = uploadState.status === "uploading" ? uploadState.progress || 0 : 100;

  return (
    <div
      onDragEnter={(event) => {
        event.preventDefault();
        setDragActive(true);
      }}
      onDragOver={(event) => {
        event.preventDefault();
        setDragActive(true);
      }}
      onDragLeave={(event) => {
        event.preventDefault();
        if (!event.currentTarget.contains(event.relatedTarget)) setDragActive(false);
      }}
      onDrop={(event) => {
        event.preventDefault();
        setDragActive(false);
        if (!disabled) handleFiles(Array.from(event.dataTransfer.files));
      }}
      className={`mb-3 rounded-xl border border-dashed px-4 py-3 transition-colors ${
        dragActive
          ? "border-[#111111] bg-[#F6F4EE]"
          : "border-[#D6D2C6] bg-[#FAF7EE]"
      } ${disabled ? "opacity-60" : ""}`}
    >
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,.pdf"
        className="hidden"
        disabled={disabled}
        onChange={(event) => {
          handleFiles(Array.from(event.target.files || []));
          event.target.value = "";
        }}
      />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button
          type="button"
          onClick={chooseFile}
          disabled={disabled}
          className="inline-flex items-center gap-2 text-left text-sm font-medium text-[#111111] disabled:cursor-not-allowed"
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-white text-[#111111] shadow-sm">
            <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 16.5V9.75m0 0 3 3m-3-3-3 3M6.75 19.5h10.5A2.25 2.25 0 0 0 19.5 17.25V6.75A2.25 2.25 0 0 0 17.25 4.5H6.75A2.25 2.25 0 0 0 4.5 6.75v10.5A2.25 2.25 0 0 0 6.75 19.5Z" />
            </svg>
          </span>
          <span>
            <span className="block">Invoice PDF</span>
            <span className="block text-xs font-normal text-[#9A9890]">{uploadState.filename || "Drop or choose a PDF"}</span>
          </span>
        </button>
        {statusText && (
          <div className="min-w-[140px] text-right">
            <div className="flex items-center justify-end gap-2">
              {busy && <SpinnerIcon className="h-4 w-4 text-[#111111]" />}
              {uploadState.status === "done" && (
                <svg className="h-4 w-4 text-emerald-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.8" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                </svg>
              )}
              <p className="text-xs font-medium text-[#6B6B66]">{statusText}</p>
            </div>
            {(busy || uploadState.status === "done") && (
              <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-[#E4E0D6]">
                <div
                  className={`h-full rounded-full bg-[#111111] transition-all ${uploadState.status !== "uploading" ? "animate-pulse" : ""}`}
                  style={{ width: `${progress}%` }}
                />
              </div>
            )}
            {uploadState.status === "extracting" && (
              <div className="mt-2 flex justify-end gap-1">
                <span className="h-1.5 w-1.5 rounded-full bg-[#9A9890] animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="h-1.5 w-1.5 rounded-full bg-[#9A9890] animate-bounce" style={{ animationDelay: "120ms" }} />
                <span className="h-1.5 w-1.5 rounded-full bg-[#9A9890] animate-bounce" style={{ animationDelay: "240ms" }} />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function InvoiceProcessingCard({ uploadState }) {
  const title = {
    creating: "Preparing invoice draft",
    uploading: `Uploading invoice ${uploadState.progress || 0}%`,
    extracting: "Reading invoice",
  }[uploadState.status] || "Processing invoice";
  const detail = {
    creating: "Setting up the transfer record",
    uploading: uploadState.filename || "Sending PDF to Cloudinary",
    extracting: "Analysing the PDF for supplier, amount, reference, and due date",
  }[uploadState.status] || uploadState.filename;
  const progress = uploadState.status === "uploading" ? uploadState.progress || 0 : 100;

  return (
    <div className="mb-5 flex justify-start">
      <div className="w-full max-w-xl rounded-xl border border-[#E4E0D6] bg-white px-5 py-4 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#F6F4EE] text-[#111111]">
            <SpinnerIcon className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-[#111111]">{title}</p>
            <p className="truncate text-xs text-[#6B6B66]">{detail}</p>
          </div>
          <div className="flex gap-1">
            <span className="h-2 w-2 rounded-full bg-[#111111] animate-bounce" style={{ animationDelay: "0ms" }} />
            <span className="h-2 w-2 rounded-full bg-[#111111] animate-bounce" style={{ animationDelay: "120ms" }} />
            <span className="h-2 w-2 rounded-full bg-[#111111] animate-bounce" style={{ animationDelay: "240ms" }} />
          </div>
        </div>
        <div className="mt-4 h-2 overflow-hidden rounded-full bg-[#E4E0D6]">
          <div
            className={`h-full rounded-full bg-[#111111] transition-all ${uploadState.status !== "uploading" ? "animate-pulse" : ""}`}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    </div>
  );
}

function currencySymbol(code) {
  const symbols = { NZD: "$", AUD: "$", USD: "$", CAD: "$", GBP: "£", EUR: "€", JPY: "¥" };
  return symbols[code] || "$";
}

const formInputClass =
  "h-9 w-full rounded-lg border border-[#E4E0D6] bg-[#FBFAF6] px-2.5 text-[13px] text-[#111111] placeholder-[#C4BFB2] transition-colors focus:border-[#111111] focus:bg-white focus:outline-none focus:ring-1 focus:ring-[#111111]";

function FormField({ label, className = "", children }) {
  return (
    <label className={`block min-w-0 ${className}`}>
      <span className="mb-1 block text-[10px] font-medium uppercase tracking-[0.08em] text-[#9A9890]">{label}</span>
      {children}
    </label>
  );
}

function FormInput({ value, onChange, placeholder, type = "text", maxLength, disabled = false }) {
  return (
    <input
      type={type}
      value={value || ""}
      maxLength={maxLength}
      placeholder={placeholder}
      disabled={disabled}
      onChange={(event) => (disabled ? undefined : onChange(event.target.value))}
      className={`${formInputClass}${disabled ? " cursor-not-allowed bg-[#F0EDE4] text-[#9A9890]" : ""}`}
    />
  );
}

function FormSelect({ value, onChange, children }) {
  return (
    <select
      value={value || ""}
      onChange={(event) => onChange(event.target.value)}
      className={formInputClass}
    >
      {children}
    </select>
  );
}

function accountLabel(account) {
  if (!account) return "";
  return `${account.name} (${account.code})`;
}

function AccountCombobox({ value, options, onChange, placeholder = "Choose account" }) {
  const listboxId = useId();
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const selectedAccount = options.find((option) => option.code === value);
  const selectedLabel = accountLabel(selectedAccount);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(selectedLabel);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (!open) setQuery(selectedLabel);
  }, [open, selectedLabel]);

  useEffect(() => {
    if (!open) return undefined;

    function handlePointerDown(event) {
      if (!wrapperRef.current?.contains(event.target)) {
        setOpen(false);
      }
    }

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [open]);

  const filteredOptions = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return options;

    return options.filter((account) => {
      const code = account.code.toLowerCase();
      const name = account.name.toLowerCase();
      return code.includes(normalizedQuery) || name.includes(normalizedQuery) || `${name} ${code}`.includes(normalizedQuery);
    });
  }, [options, query]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  const chooseAccount = (account) => {
    onChange(account.code, account);
    setQuery(accountLabel(account));
    setOpen(false);
    inputRef.current?.blur();
  };

  const clearAccount = ({ preserveQuery = false } = {}) => {
    onChange("", null);
    if (!preserveQuery) setQuery("");
  };

  const handleKeyDown = (event) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((index) => Math.min(index + 1, Math.max(filteredOptions.length - 1, 0)));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((index) => Math.max(index - 1, 0));
    } else if (event.key === "Enter" && open && filteredOptions[activeIndex]) {
      event.preventDefault();
      chooseAccount(filteredOptions[activeIndex]);
    } else if (event.key === "Escape") {
      setOpen(false);
      setQuery(selectedLabel);
      inputRef.current?.blur();
    }
  };

  return (
    <div ref={wrapperRef} className="relative">
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={listboxId}
        value={open ? query : selectedLabel}
        placeholder={placeholder}
        onFocus={(event) => {
          setOpen(true);
          setQuery(selectedLabel);
          requestAnimationFrame(() => event.currentTarget.select());
        }}
        onChange={(event) => {
          const nextQuery = event.target.value;
          const normalizedQuery = nextQuery.trim().toLowerCase();
          const exactAccount = options.find((account) => (
            account.code.toLowerCase() === normalizedQuery || accountLabel(account).toLowerCase() === normalizedQuery
          ));

          setQuery(nextQuery);
          setOpen(true);
          if (exactAccount) {
            onChange(exactAccount.code, exactAccount);
          } else if (normalizedQuery === "") {
            clearAccount();
          } else if (value) {
            clearAccount({ preserveQuery: true });
          }
        }}
        onKeyDown={handleKeyDown}
        className={`${formInputClass} pr-9`}
      />
      <button
        type="button"
        aria-label="Show Xero accounts"
        onClick={() => {
          setOpen((current) => !current);
          inputRef.current?.focus();
        }}
        className="absolute right-1 top-1 flex h-7 w-7 items-center justify-center rounded-md text-[#9A9890] transition-colors hover:bg-[#F6F4EE] hover:text-[#111111]"
      >
        <svg className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.8" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
        </svg>
      </button>
      {open && (
        <div
          id={listboxId}
          role="listbox"
          className="absolute z-50 mt-1 max-h-64 w-full overflow-y-auto rounded-lg border border-[#D6D2C6] bg-white py-1 shadow-lg"
        >
          {filteredOptions.length > 0 ? (
            filteredOptions.map((account, index) => {
              const active = index === activeIndex;
              const selected = account.code === value;
              return (
                <button
                  key={account.code}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  onMouseEnter={() => setActiveIndex(index)}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    chooseAccount(account);
                  }}
                  className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-[13px] transition-colors ${
                    active ? "bg-[#F6F4EE] text-[#111111]" : "text-[#3A3A36] hover:bg-[#FAF7EE]"
                  }`}
                >
                  <span className="min-w-0 truncate">{account.name}</span>
                  <span className={`shrink-0 rounded-md border px-1.5 py-0.5 text-[11px] font-medium ${
                    selected ? "border-[#111111] text-[#111111]" : "border-[#E4E0D6] text-[#9A9890]"
                  }`}>
                    {account.code}
                  </span>
                </button>
              );
            })
          ) : (
            <p className="px-3 py-2 text-[13px] text-[#9A9890]">No matching accounts</p>
          )}
        </div>
      )}
    </div>
  );
}

function beneficiarySubtitle(beneficiary) {
  return [beneficiary.bank_name, beneficiary.account_number].filter(Boolean).join(" · ");
}

function BeneficiaryCombobox({ value, selectedId, options, onSelect, onText, placeholder = "Supplier name" }) {
  const listboxId = useId();
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value || "");
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (!open) setQuery(value || "");
  }, [open, value]);

  useEffect(() => {
    if (!open) return undefined;

    function handlePointerDown(event) {
      if (!wrapperRef.current?.contains(event.target)) {
        setOpen(false);
      }
    }

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [open]);

  const filteredOptions = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return options;

    return options.filter((beneficiary) => {
      const name = (beneficiary.name || "").toLowerCase();
      const bank = (beneficiary.bank_name || "").toLowerCase();
      const account = (beneficiary.account_number || "").toLowerCase();
      return name.includes(normalizedQuery) || bank.includes(normalizedQuery) || account.includes(normalizedQuery);
    });
  }, [options, query]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  const chooseBeneficiary = (beneficiary) => {
    onSelect(beneficiary);
    setQuery(beneficiary.name || "");
    setOpen(false);
    inputRef.current?.blur();
  };

  const handleKeyDown = (event) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((index) => Math.min(index + 1, Math.max(filteredOptions.length - 1, 0)));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((index) => Math.max(index - 1, 0));
    } else if (event.key === "Enter" && open && filteredOptions[activeIndex]) {
      event.preventDefault();
      chooseBeneficiary(filteredOptions[activeIndex]);
    } else if (event.key === "Escape") {
      setOpen(false);
      setQuery(value || "");
      inputRef.current?.blur();
    }
  };

  return (
    <div ref={wrapperRef} className="relative">
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={listboxId}
        value={open ? query : (value || "")}
        placeholder={placeholder}
        onFocus={(event) => {
          setOpen(true);
          setQuery(value || "");
          requestAnimationFrame(() => event.currentTarget.select());
        }}
        onChange={(event) => {
          const nextQuery = event.target.value;
          setQuery(nextQuery);
          setOpen(true);
          onText(nextQuery);
        }}
        onKeyDown={handleKeyDown}
        className={`${formInputClass} pr-9`}
      />
      <button
        type="button"
        aria-label="Show beneficiaries"
        onClick={() => {
          setOpen((current) => !current);
          inputRef.current?.focus();
        }}
        className="absolute right-1 top-1 flex h-7 w-7 items-center justify-center rounded-md text-[#9A9890] transition-colors hover:bg-[#F6F4EE] hover:text-[#111111]"
      >
        <svg className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.8" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
        </svg>
      </button>
      {open && (
        <div
          id={listboxId}
          role="listbox"
          className="absolute z-50 mt-1 max-h-64 w-full overflow-y-auto rounded-lg border border-[#D6D2C6] bg-white py-1 shadow-lg"
        >
          {filteredOptions.length > 0 ? (
            filteredOptions.map((beneficiary, index) => {
              const active = index === activeIndex;
              const selected = beneficiary.id === selectedId;
              const subtitle = beneficiarySubtitle(beneficiary);
              return (
                <button
                  key={beneficiary.id}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  onMouseEnter={() => setActiveIndex(index)}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    chooseBeneficiary(beneficiary);
                  }}
                  className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-[13px] transition-colors ${
                    active ? "bg-[#F6F4EE] text-[#111111]" : "text-[#3A3A36] hover:bg-[#FAF7EE]"
                  }`}
                >
                  <span className="min-w-0">
                    <span className="block truncate">{beneficiary.name}</span>
                    {subtitle ? (
                      <span className="block truncate text-[11px] text-[#9A9890]">{subtitle}</span>
                    ) : null}
                  </span>
                  {beneficiary.account_currency ? (
                    <span className={`shrink-0 rounded-md border px-1.5 py-0.5 text-[11px] font-medium ${
                      selected ? "border-[#111111] text-[#111111]" : "border-[#E4E0D6] text-[#9A9890]"
                    }`}>
                      {beneficiary.account_currency}
                    </span>
                  ) : null}
                </button>
              );
            })
          ) : (
            <p className="px-3 py-2 text-[13px] text-[#9A9890]">No matching beneficiaries</p>
          )}
        </div>
      )}
    </div>
  );
}

function TransferDraftCard({
  transfer,
  xeroAccountOptions,
  xeroTaxOptions,
  beneficiaryOptions,
  environment,
  onEnvironmentChange,
  environmentDisabled,
  isDev,
  onFieldChange,
  onFieldsChange,
  onBeneficiarySelect,
  onPrepare,
  disabled,
  preparing,
}) {
  if (!transfer) return null;

  const hasBeneficiary = Boolean(transfer.beneficiary_id || transfer.beneficiary_name);
  const canPrepare = hasBeneficiary &&
    transfer.transfer_amount &&
    transfer.transfer_currency &&
    transfer.reference &&
    transfer.xero_account_code &&
    transfer.xero_tax_type;
  const waiting = transfer.status === "pending_confirmation";
  const symbol = currencySymbol(transfer.transfer_currency);
  const fxMismatch = transfer.source_currency && transfer.source_currency !== transfer.transfer_currency;
  const accountOptions = xeroAccountOptions || [];
  const taxOptions = xeroTaxOptions || [];
  const beneficiaryStatus = transfer.beneficiary_id
    ? `Matched · ${transfer.beneficiary_id}`
    : transfer.beneficiary_name
      ? `Will look up in ${airwallexEnvironmentLabel(transfer.environment)} on review`
      : "Enter supplier name";
  const duplicateWarning = transfer.duplicate_reference;
  const duplicateWarningText = duplicateWarning ? duplicateReferenceText(duplicateWarning) : "";

  return (
    <div className="relative z-10 mb-5 overflow-visible rounded-xl border border-[#D6D2C6] bg-white shadow-sm">
      <div className="flex items-center justify-between gap-3 border-b border-[#EFEBE1] px-4 py-2.5">
        <EnvironmentSelector
          environment={environment}
          onChange={onEnvironmentChange}
          disabled={environmentDisabled}
          isDev={isDev}
        />
        <div className="flex items-center gap-3">
          <span className="text-[10px] font-medium uppercase tracking-[0.08em] text-[#9A9890]">Invoice draft</span>
          <span className="shrink-0 rounded-full bg-[#F6F4EE] px-2 py-0.5 text-[11px] font-medium text-[#6B6B66]">
            {transfer.display_status || transfer.status}
          </span>
        </div>
      </div>

      {duplicateWarning && (
        <div className="mx-4 mt-4 flex items-start gap-2.5 rounded-lg border border-red-300 bg-red-50 px-3 py-2.5">
          <svg className="mt-0.5 h-4 w-4 shrink-0 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.8" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
          </svg>
          <div className="min-w-0 text-[12px] leading-snug text-red-700">
            <p className="font-semibold uppercase tracking-[0.04em]">Possible duplicate payment</p>
            <p className="mt-0.5">{duplicateWarningText}</p>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-end gap-3 px-4 py-4">
        <FormField label="Beneficiary" className="flex-1 basis-44">
          <BeneficiaryCombobox
            value={transfer.beneficiary_name}
            selectedId={transfer.beneficiary_id}
            options={beneficiaryOptions || []}
            onSelect={(beneficiary) => onBeneficiarySelect(beneficiary)}
            onText={(name) => {
              onFieldsChange({
                beneficiary_name: name,
                beneficiary_id: "",
                beneficiary_bank_name: "",
                beneficiary_account_number: "",
                beneficiary_entity_type: "",
              });
            }}
          />
        </FormField>
        <FormField label="Amount" className="w-36 shrink-0">
          <div className="relative">
            <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[13px] text-[#6B6B66]">{symbol}</span>
            <input
              type="number"
              value={transfer.transfer_amount || ""}
              placeholder="0.00"
              onChange={(event) => onFieldChange("transfer_amount", event.target.value)}
              className="h-9 w-full rounded-lg border border-[#E4E0D6] bg-[#FBFAF6] pl-6 pr-11 text-[13px] font-semibold text-[#111111] placeholder-[#C4BFB2] transition-colors focus:border-[#111111] focus:bg-white focus:outline-none focus:ring-1 focus:ring-[#111111]"
            />
            <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] font-medium uppercase tracking-wide text-[#9A9890]">{transfer.transfer_currency}</span>
          </div>
        </FormField>
        <FormField label={transfer.reference_locked ? "Reference (locked)" : "Reference"} className="flex-1 basis-44">
          <FormInput
            value={transfer.reference}
            placeholder="INV-123 FRAMEFOX"
            disabled={Boolean(transfer.reference_locked)}
            onChange={(value) => onFieldChange("reference", value)}
          />
          {transfer.reference_locked ? (
            <span className="mt-1 block text-[10px] text-[#9A9890]">Locked by beneficiary</span>
          ) : null}
        </FormField>
        <FormField label="Date" className="w-40 shrink-0">
          <FormInput
            type="date"
            value={transfer.transfer_date}
            onChange={(value) => onFieldChange("transfer_date", value)}
          />
        </FormField>
        <FormField label="Xero account" className="flex-1 basis-56">
          <AccountCombobox
            value={transfer.xero_account_code}
            options={accountOptions}
            onChange={(value, account) => {
              onFieldsChange({
                xero_account_code: value,
                xero_account_name: account?.name || "",
              });
            }}
          />
        </FormField>
        <FormField label="Xero tax" className="w-52 shrink-0">
          <FormSelect
            value={transfer.xero_tax_type}
            onChange={(value) => {
              const taxRate = taxOptions.find((option) => option.tax_type === value);
              onFieldsChange({
                xero_tax_type: value,
                xero_tax_name: taxRate?.name || "",
              });
            }}
          >
            <option value="">Choose tax</option>
            {taxOptions.map((taxRate) => (
              <option key={taxRate.tax_type} value={taxRate.tax_type}>
                {taxRate.name}
              </option>
            ))}
          </FormSelect>
        </FormField>
      </div>

      <div className="flex items-center justify-between gap-3 rounded-b-xl border-t border-[#EFEBE1] bg-[#FAF7EE] px-4 py-2.5">
        <p className="min-w-0 truncate text-[11px] text-[#9A9890]">
          {beneficiaryStatus}
          {fxMismatch ? ` · Source ${transfer.source_currency}` : ""}
        </p>
        <button
          type="button"
          onClick={() => onPrepare(transfer)}
          disabled={disabled || preparing || waiting || !canPrepare}
          className="inline-flex h-8 shrink-0 items-center justify-center gap-1.5 rounded-md bg-[#111111] px-3 text-[12px] font-medium text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {waiting ? "Awaiting approval" : preparing ? "Preparing" : "Review transfer"}
        </button>
      </div>
    </div>
  );
}

export default function AirwallexChat({
  chatId,
  messages: initialMessages,
  environment: initialEnvironment,
  isDev,
  draftTransfers: initialDraftTransfers,
  xeroAccountOptions,
  xeroTaxOptions,
}) {
  const [messages, setMessages] = useState(initialMessages || []);
  const [transfers, setTransfers] = useState(initialDraftTransfers || []);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploadState, setUploadState] = useState({ status: "idle", progress: 0, filename: null });
  const [preparingTransferId, setPreparingTransferId] = useState(null);
  const [error, setError] = useState(null);
  const [environment, setEnvironment] = useState(initialEnvironment || (isDev ? "sandbox" : "nz"));
  const [beneficiaryOptions, setBeneficiaryOptions] = useState([]);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const latestMessage = messages[messages.length - 1];
  const approvalPromptMessageId = latestMessage && isTransferApprovalPrompt(latestMessage) ? latestMessage.id : null;
  const uploadBusy = BUSY_UPLOAD_STATUSES.includes(uploadState.status);
  const activeTransfer = transfers.find((transfer) => {
    if (!ACTIVE_TRANSFER_STATUSES.includes(transfer.status)) return false;
    if (uploadBusy) return true;
    return !(transfer.source === "invoice_pdf" && transfer.status === "draft" && (transfer.attachments || []).length === 0);
  });
  const showDraftCard = shouldShowTransferDraft(activeTransfer, uploadBusy);
  const environmentLocked = loading || uploadBusy || Boolean(activeTransfer && activeTransfer.status !== "draft");

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, loading, uploadState.status, showDraftCard]);

  useEffect(() => {
    if (!loading) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [loading]);

  useEffect(() => {
    let cancelled = false;

    const loadBeneficiaries = async () => {
      try {
        const response = await fetch(
          `/airwallex_chats/${chatId}/beneficiaries?environment=${encodeURIComponent(environment)}`,
          { headers: { Accept: "application/json" } }
        );
        if (!response.ok) return;
        const data = await response.json();
        if (!cancelled) setBeneficiaryOptions(data.beneficiaries || []);
      } catch (err) {
        // Non-fatal: free-text beneficiary entry still works without the lookup list.
      }
    };

    loadBeneficiaries();
    return () => {
      cancelled = true;
    };
  }, [chatId, environment]);

  const csrfToken = () => {
    const meta = document.querySelector('meta[name="csrf-token"]');
    return meta ? meta.getAttribute("content") : "";
  };

  const handleEnvironmentChange = async (newEnv) => {
    if (newEnv === environment || environmentLocked) return;

    const draftTransfer = activeTransfer && activeTransfer.status === "draft" ? activeTransfer : null;

    try {
      if (draftTransfer) {
        // Rescoping an active draft: this also syncs the chat environment server-side
        // and resets the currency + stale beneficiary match to the new country.
        const response = await fetch(
          `/airwallex_transfers/${draftTransfer.id}/change_environment`,
          {
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
              "X-CSRF-Token": csrfToken(),
            },
            body: JSON.stringify({ environment: newEnv }),
          }
        );

        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.error || "Failed to update environment");
        }

        updateTransferState(data.transfer);
        setEnvironment(newEnv);
        setError(null);
        return;
      }

      const response = await fetch(
        `/airwallex_chats/${chatId}/update_environment`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": csrfToken(),
          },
          body: JSON.stringify({ environment: newEnv }),
        }
      );

      if (response.ok) {
        setEnvironment(newEnv);
        setError(null);
      }
    } catch (err) {
      setError(err.message || "Failed to update environment");
    }
  };

  const updateTransferState = (transfer) => {
    setTransfers((prev) => upsertTransfer(prev, transfer));
  };

  const handleInvoiceFile = async (file) => {
    if (loading || uploadBusy) return;

    const isPdf = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
    if (!isPdf) {
      setError("Please upload a PDF invoice.");
      return;
    }

    setError(null);
    setUploadState({ status: "creating", progress: 0, filename: file.name });

    let draftTransferId = null;
    let attachmentRecorded = false;

    try {
      const draftData = await postJson(
        `/airwallex_chats/${chatId}/transfers`,
        { source: "invoice_pdf" },
        csrfToken()
      );
      draftTransferId = draftData.transfer.id;
      updateTransferState(draftData.transfer);

      setUploadState({ status: "uploading", progress: 0, filename: file.name });
      const cloudinaryResult = await uploadPdfToCloudinary(file, draftData.cloudinary, (progress) => {
        setUploadState({ status: "uploading", progress, filename: file.name });
      });

      const attachmentData = await postJson(
        `/airwallex_transfers/${draftData.transfer.id}/attachments`,
        {
          attachment: {
            ...cloudinaryResult,
            filename: file.name,
            content_type: file.type,
          },
        },
        csrfToken()
      );
      attachmentRecorded = true;
      updateTransferState(attachmentData.transfer);

      setUploadState({ status: "extracting", progress: 100, filename: file.name });
      const extractionData = await postJson(
        `/airwallex_transfers/${draftData.transfer.id}/extract_invoice`,
        {},
        csrfToken()
      );
      updateTransferState(extractionData.transfer);
      setUploadState({ status: "done", progress: 100, filename: file.name });
      setTimeout(() => setUploadState({ status: "idle", progress: 0, filename: null }), 1800);
    } catch (err) {
      setError(err.message);
      if (draftTransferId && !attachmentRecorded) {
        setTransfers((prev) => prev.filter((transfer) => transfer.id !== draftTransferId));
      }
      setUploadState({ status: "idle", progress: 0, filename: null });
    }
  };

  const updateDraftField = (field, value) => {
    if (!activeTransfer) return;

    setTransfers((prev) =>
      prev.map((transfer) => (transfer.id === activeTransfer.id ? { ...transfer, [field]: value } : transfer))
    );
  };

  const updateDraftFields = (fields) => {
    if (!activeTransfer) return;

    setTransfers((prev) =>
      prev.map((transfer) => (transfer.id === activeTransfer.id ? { ...transfer, ...fields } : transfer))
    );
  };

  // When a beneficiary is chosen from the list, pull their most recent Xero coding
  // and fill any blank account/tax fields, so a draft loaded without a beneficiary
  // gets the same defaults the server applies on save.
  const applyBeneficiaryXeroDefaults = async (transferId, beneficiaryId) => {
    if (!transferId || !beneficiaryId) return;

    try {
      const response = await fetch(
        `/airwallex_chats/${chatId}/beneficiary_xero_defaults?beneficiary_id=${encodeURIComponent(beneficiaryId)}&environment=${encodeURIComponent(environment)}`,
        { headers: { Accept: "application/json" } }
      );
      if (!response.ok) return;
      const data = await response.json();

      setTransfers((prev) =>
        prev.map((transfer) => {
          if (transfer.id !== transferId) return transfer;
          // Bail if the user changed the beneficiary while this request was in flight.
          if (transfer.beneficiary_id !== beneficiaryId) return transfer;

          const next = { ...transfer };
          if (!next.xero_account_code && data.xero_account_code) {
            next.xero_account_code = data.xero_account_code;
            next.xero_account_name = data.xero_account_name || "";
          }
          if (!next.xero_tax_type && data.xero_tax_type) {
            next.xero_tax_type = data.xero_tax_type;
            next.xero_tax_name = data.xero_tax_name || "";
          }
          return next;
        })
      );
    } catch (err) {
      // Non-fatal: the user can still pick account/tax manually.
    }
  };

  const selectDraftBeneficiary = (beneficiary) => {
    if (!activeTransfer) return;

    updateDraftFields({
      beneficiary_id: beneficiary.id,
      beneficiary_name: beneficiary.name,
      beneficiary_bank_name: beneficiary.bank_name || "",
      beneficiary_account_number: beneficiary.account_number || "",
      beneficiary_entity_type: beneficiary.entity_type || "",
    });

    if (beneficiary.id) {
      applyBeneficiaryXeroDefaults(activeTransfer.id, beneficiary.id);
    }
  };

  const prepareTransfer = async (transfer) => {
    if (!transfer || preparingTransferId) return;

    setError(null);
    setPreparingTransferId(transfer.id);

    try {
      const data = await postJson(
        `/airwallex_transfers/${transfer.id}/prepare_confirmation`,
        {
          airwallex_transfer: {
            beneficiary_name: transfer.beneficiary_name,
            beneficiary_id: transfer.beneficiary_id,
            transfer_amount: transfer.transfer_amount,
            reason: transfer.reason || "goods_purchased",
            reference: transfer.reference,
            transfer_date: transfer.transfer_date,
            transfer_method: transfer.transfer_method || "LOCAL",
            xero_account_code: transfer.xero_account_code,
            xero_tax_type: transfer.xero_tax_type,
          },
        },
        csrfToken()
      );

      updateTransferState(data.transfer);
      if (data.message) {
        setMessages((prev) => [...prev, data.message]);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setPreparingTransferId(null);
    }
  };

  const submitMessage = async (text) => {
    const trimmedText = text.trim();
    if (!trimmedText || loading) return;

    setError(null);
    setInput("");

    // Optimistically add user message
    const userMessage = {
      id: Date.now(),
      role: "user",
      content: trimmedText,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, userMessage]);
    setLoading(true);

    try {
      const response = await fetch(
        `/airwallex_chats/${chatId}/send_message`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": csrfToken(),
          },
          body: JSON.stringify({ message: trimmedText }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to send message");
      }

      const assistantMessage = {
        id: Date.now() + 1,
        ...data.message,
      };
      const extraMessages = Array.isArray(data.messages)
        ? data.messages.map((m, i) => ({ id: Date.now() + 2 + i, ...m }))
        : [];
      setMessages((prev) => [...prev, assistantMessage, ...extraMessages]);
      if (data.transfer) {
        updateTransferState(data.transfer);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || loading) return;

    submitMessage(text);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Messages area */}
      <div className="flex-1 overflow-y-auto px-6 py-6 bg-[#FAF7EE]">
        {showDraftCard && (
          <TransferDraftCard
            transfer={activeTransfer}
            xeroAccountOptions={xeroAccountOptions}
            xeroTaxOptions={xeroTaxOptions}
            beneficiaryOptions={beneficiaryOptions}
            environment={environment}
            onEnvironmentChange={handleEnvironmentChange}
            environmentDisabled={loading || uploadBusy || activeTransfer.status !== "draft"}
            isDev={isDev}
            onFieldChange={updateDraftField}
            onFieldsChange={updateDraftFields}
            onBeneficiarySelect={selectDraftBeneficiary}
            onPrepare={prepareTransfer}
            disabled={loading || uploadBusy}
            preparing={preparingTransferId === activeTransfer.id}
          />
        )}

        {uploadBusy && <InvoiceProcessingCard uploadState={uploadState} />}

        {messages.length === 0 && !loading && !activeTransfer && !uploadBusy && (
          <ScheduledTransfers chatId={chatId} environment={environment} />
        )}

        {messages.map((msg) => (
          <MessageBubble
            key={msg.id}
            message={msg}
            showApprovalActions={msg.id === approvalPromptMessageId && !loading}
            onQuickReply={submitMessage}
            disabled={loading}
          />
        ))}

        {loading && <LoadingIndicator />}

        <div ref={messagesEndRef} />
      </div>

      {/* Error banner */}
      {error && (
        <div className="px-6 py-3 bg-red-50 border-t border-red-200">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Input area */}
      <div className="border-t border-[#E4E0D6] bg-white px-6 py-4">
        <div className="flex items-center justify-between mb-3">
          <EnvironmentSelector
            chatId={chatId}
            environment={environment}
            onChange={handleEnvironmentChange}
            disabled={environmentLocked}
            isDev={isDev}
          />
          <span className="text-xs text-[#9A9890]">
            {environment === "sandbox" ? "Demo data" : environment.toUpperCase() + " Production"}
          </span>
        </div>
        <InvoiceUploadPanel
          uploadState={uploadState}
          onFile={handleInvoiceFile}
          disabled={loading || uploadBusy || Boolean(activeTransfer)}
        />
        <form onSubmit={sendMessage} className="flex items-end space-x-3">
          <div className="flex-1">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage(e);
                }
              }}
              placeholder="Ask about your Airwallex account..."
              rows="1"
              className="block w-full resize-none rounded-xl border border-[#E4E0D6] bg-[#FAF7EE] px-4 py-3 text-sm text-[#111111] placeholder-[#9A9890] focus:border-[#111111] focus:bg-white focus:outline-none focus:ring-1 focus:ring-[#111111] transition-colors duration-150"
              disabled={loading}
              style={{
                minHeight: "44px",
                maxHeight: "120px",
              }}
              onInput={(e) => {
                e.target.style.height = "auto";
                e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px";
              }}
            />
          </div>
          <button
            type="submit"
            disabled={loading || !input.trim()}
            className="flex-shrink-0 inline-flex items-center justify-center h-11 w-11 rounded-xl bg-[#111111] text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[#111111] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity duration-150"
          >
            <svg
              className="h-5 w-5"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 12 3.269 3.125A59.769 59.769 0 0 1 21.485 12 59.768 59.768 0 0 1 3.27 20.875L5.999 12Zm0 0h7.5"
              />
            </svg>
          </button>
        </form>
      </div>
    </div>
  );
}
